import type { DrawerBounds } from "~/features/documents/editor/selectionActionsStore";

/**
 * Where the results drawer sits. Pure geometry, unit-testable, no DOM.
 *
 * This is the ONE place in the wave exempt from the logical-utilities rule, because it is not
 * CSS: it computes pixel offsets against a viewport, and a viewport has a left and a right no
 * matter which way the text runs. The PREFERENCE is still logical — `start`/`end` mean
 * inline-start/inline-end — so it is resolved against the direction here, once.
 *
 * `bottom` is implemented rather than declared. kms offers it in settings and has no branch
 * for it, so it silently behaves as `auto`; shipping the same dead option twice is not parity.
 */

export type DrawerPreference = "auto" | "start" | "end" | "bottom";

export interface DrawerLayout {
  /** Physical CSS, ready for a style object. */
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  width: number | string;
  maxHeight: number | string;
  /** True on the mobile bottom sheet, which is styled differently (rounded top, full width). */
  sheet: boolean;
}

const WIDTH = 360;
const MIN_HEIGHT = 200;
const MARGIN = 12;

export function drawerLayout(
  bounds: DrawerBounds | null,
  preference: DrawerPreference,
  options: { viewportWidth: number; viewportHeight: number; isMobile: boolean; rtl: boolean },
): DrawerLayout {
  const { viewportWidth, viewportHeight, isMobile, rtl } = options;

  // A phone has no room beside anything: a bottom sheet is the only honest layout.
  if (isMobile || preference === "bottom") {
    return {
      left: 0,
      right: 0,
      bottom: 0,
      width: "100%",
      maxHeight: Math.round(viewportHeight * 0.6),
      sheet: true,
    };
  }

  // No anchor (a keyboard-invoked action, a collapsed selection) — centre it.
  if (!bounds) {
    return {
      top: Math.max(MARGIN, Math.round((viewportHeight - MIN_HEIGHT * 2) / 2)),
      left: Math.max(MARGIN, Math.round((viewportWidth - WIDTH) / 2)),
      width: WIDTH,
      maxHeight: viewportHeight - MARGIN * 2,
      sheet: false,
    };
  }

  const roomAfter = viewportWidth - bounds.right - MARGIN;
  const roomBefore = bounds.left - MARGIN;
  // `start`/`end` are reading-direction words; resolve them once, here.
  const wantsAfter =
    preference === "auto"
      ? roomAfter >= WIDTH || roomAfter >= roomBefore
      : rtl
        ? preference === "start"
        : preference === "end";

  const left =
    wantsAfter && roomAfter >= WIDTH
      ? bounds.right + MARGIN
      : roomBefore >= WIDTH
        ? bounds.left - WIDTH - MARGIN
        : // Neither side fits: sit under the selection instead of hanging off the edge.
          Math.max(MARGIN, Math.min(bounds.left, viewportWidth - WIDTH - MARGIN));

  const roomBelow = viewportHeight - bounds.top - MARGIN;
  const vertical =
    roomBelow >= MIN_HEIGHT
      ? { top: bounds.top }
      : // Pinned to the bottom rather than pushed off it.
        { bottom: MARGIN };

  return {
    ...vertical,
    left: Math.max(MARGIN, Math.min(left, viewportWidth - WIDTH - MARGIN)),
    width: WIDTH,
    maxHeight: viewportHeight - MARGIN * 2,
    sheet: false,
  };
}
